import React, { useState, useEffect, useMemo } from 'react';
import { Search, ShoppingBag, Video, Filter, X, Loader2, SlidersHorizontal, ShieldAlert, LogOut, Mail } from 'lucide-react';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth'; 
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CartProvider } from './hooks/useCart';
import { NavigationProvider } from './contexts/NavigationContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { AuthProvider, useAuth } from './hooks/useAuth';
// IMPORT EXPORTED AUTH FROM YOUR FIREBASE FILE
import { db, auth } from './firebase'; 
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ProductCard } from './components/ProductCard';
import { CartSidebar } from './components/CartSidebar';
import { Checkout } from './components/Checkout';
import { Consult } from './components/Consult';
import { ProductDetails } from './components/ProductDetails';
import { OrdersPage } from './components/OrdersPage';
import { AdminPanel } from './components/AdminPanel';
import { Profile } from './components/Profile';
import { Footer } from './components/Footer';
import { LoginModal } from './components/LoginModal';
import AppLoadingScreen from './components/AppLoadingScreen';
import { Product } from './types';

/* ---------------- GLOBAL SECURITY & RATE LIMITING ---------------- */

export const SecurityProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();

  // 1. Firebase Anonymous Auth & Identity Sync (FIXED WITH LISTENER)
  useEffect(() => {
    // Listen for Firebase Auth state to resolve before doing anything
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      let uid = firebaseUser?.uid;

      // If no user exists, create an anonymous one!
      if (!firebaseUser) {
        try {
          const userCred = await signInAnonymously(auth);
          uid = userCred.user.uid;
          console.log("Generated New Anonymous User:", uid);
        } catch (error) {
          console.error("Anon Auth Error:", error);
          return;
        }
      }

      if (!uid) return;

      // Local tracking ID backup
      let deviceId = localStorage.getItem('device_anon_id');
      if (!deviceId) {
        deviceId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('device_anon_id', deviceId);
      }

      // Grab custom identity (email/phone) from your useAuth if logged in
      const currentIdentity = user?.email || user?.mobile || user?.uid || "anonymous";

      try {
        // Sync to device_tracking using the FIREBASE UID
        const deviceRef = doc(db, 'device_tracking', uid);
        const docSnap = await getDoc(deviceRef);

        if (!docSnap.exists()) {
          await setDoc(deviceRef, {
            anonymousId: deviceId, // local reference
            firebaseUid: uid,      // official reference
            identity: currentIdentity,
            lastSeen: serverTimestamp()
          });
        } else {
          const data = docSnap.data();
          // Update identity if it changed
          if (data.identity !== currentIdentity && currentIdentity !== "anonymous") {
            await updateDoc(deviceRef, {
              identity: currentIdentity,
              lastSeen: serverTimestamp()
            });
          }
        }
      } catch (error) {
        // Silently fail if blocked by rules
      }
    });

    return () => unsubscribe();
  }, [user]);

  // 2. Global Action Interceptor (UX Spam Filter)
  useEffect(() => {
    const handleGlobalAction = async (e: MouseEvent | SubmitEvent) => {
      const target = e.target as HTMLElement;
      const isAction = e.type === 'submit' || target.closest('button') || target.closest('a');

      if (!isAction) return;

      if (user && ((user as any).isAdmin || (user as any).isConsultant)) return; 

      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const now = Date.now();
      const historyStr = localStorage.getItem('action_history');
      let history: number[] = historyStr ? JSON.parse(historyStr) : [];

      history = history.filter(t => now - t < 120000); 
      history.push(now);
      localStorage.setItem('action_history', JSON.stringify(history));

      const lastMinuteCount = history.filter(t => now - t < 60000).length;

      // BAN THRESHOLD
      if (lastMinuteCount >= 40) {
        e.stopPropagation(); 
        e.preventDefault();

        try {
          const deviceRef = doc(db, 'device_tracking', uid);
          const deviceSnap = await getDoc(deviceRef);

          if (deviceSnap.exists()) {
            const targetIdentity = deviceSnap.data().identity;
            
            // Ban the actual user account if an identity is attached
            if (targetIdentity && targetIdentity !== "anonymous") {
              await updateDoc(doc(db, 'users', targetIdentity), {
                isBanned: true,
                banReason: "Automated Security Protocol: Malicious Interaction Rate Exceeded."
              });
            }
            // Ban the Anonymous UID in DB
            await updateDoc(deviceRef, { isBanned: true });
            logout(); 
          }
        } catch (err) {}

        showNotification("SECURITY ALERT: Device banned for malicious activity.", "error");
        return;
      }

      // WARNING THRESHOLD
      if (history.length >= 30) {
        e.stopPropagation(); 
        e.preventDefault();
        showNotification("Rate limit exceeded. Please slow down.", "error");
        return;
      }
    };

    document.addEventListener('click', handleGlobalAction, true);
    document.addEventListener('submit', handleGlobalAction, true);

    return () => {
      document.removeEventListener('click', handleGlobalAction, true);
      document.removeEventListener('submit', handleGlobalAction, true);
    };
  }, [logout, showNotification, user]);

  return <>{children}</>;
};

/* ---------------- BAN SYSTEM (Integrated) ---------------- */

const useBanCheck = () => {
  const [isBanned, setIsBanned] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [banReason, setBanReason] = useState<string | undefined>();

  useEffect(() => {
    const checkBanStatus = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        
        if (!storedUser) {
          setIsLoading(false);
          return;
        }

        const parsedUser = JSON.parse(storedUser);
        const identifier = parsedUser.email || parsedUser.uid || parsedUser.mobile;

        if (!identifier) {
          setIsLoading(false);
          return;
        }

        const userRef = doc(db, 'users', identifier);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.isBanned === true) {
            setIsBanned(true);
            setBanReason(userData.banReason);
          } else {
             setIsBanned(false);
          }
        }
      } catch (error) {
        console.error("Failed to verify account standing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkBanStatus();
  }, []);

  return { isBanned, isLoading, banReason };
};

const BanModal: React.FC<{ banReason?: string }> = ({ banReason }) => {
  const { logout } = useAuth();

  return (
    <div className="fixed inset-0 z-[10000] bg-stone-900/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 text-center">
      <div className="max-w-md w-full bg-white rounded-[2rem] p-8 sm:p-10 shadow-2xl border border-red-100 animate-[scaleIn_0.3s_ease-out] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 via-red-600 to-red-500"></div>
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 relative">
          <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-20"></div>
          <ShieldAlert className="text-red-600 relative z-10" size={40} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-serif font-black text-stone-900 uppercase tracking-tighter mb-4">
          Account Suspended
        </h2>
        <p className="text-sm text-stone-600 font-medium leading-relaxed mb-6">
          Your access to Devine Stone has been restricted due to violations of our community protocols or terms of service.
        </p>
        {banReason && (
          <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 mb-8 text-left">
            <p className="text-[10px] font-black uppercase text-stone-400 tracking-widest mb-1">Reason for Suspension</p>
            <p className="text-xs font-medium text-stone-800">{banReason}</p>
          </div>
        )}
        <div className="space-y-3">
          <button onClick={logout} className="w-full bg-stone-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] sm:text-xs flex items-center justify-center gap-2 hover:bg-stone-800 transition-colors shadow-lg">
            <LogOut size={16} /> Secure Logout
          </button>
          <a href="mailto:support@devinestone.in" className="w-full bg-white text-stone-600 border border-stone-200 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] sm:text-xs flex items-center justify-center gap-2 hover:bg-stone-50 transition-colors">
            <Mail size={16} /> Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

/* ---------------- HOME VIEW ---------------- */

const HomeView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'store' | 'consult'>('store');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string>('');

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [sortOrder, setSortOrder] = useState<'none' | 'asc' | 'desc'>('none');

  const [tempCategories, setTempCategories] = useState<string[]>([]);
  const [tempPriceRange, setTempPriceRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [tempSortOrder, setTempSortOrder] = useState<'none' | 'asc' | 'desc'>('none');

  useEffect(() => {
    const load = async () => {
      try {
        setProductsError('');
        const snap = await getDocs(collection(db, 'products'));
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Product, 'id'>) }));
        setProducts(list);
      } catch (e: any) {
        setProducts([]);
        setProductsError(e?.message || 'Failed to load products');
      } finally {
        setIsLoadingProducts(false);
      }
    };
    load();
  }, []);

  const openFilterModal = () => {
    setTempCategories(selectedCategories);
    setTempPriceRange(priceRange);
    setTempSortOrder(sortOrder);
    setIsFilterOpen(true);

    if (availableCategories.length === 0) {
      setIsFetchingCategories(true);
      setTimeout(() => {
        const uniqueCategories = Array.from(new Set(products.map(p => p.category))).filter(Boolean);
        setAvailableCategories(uniqueCategories);
        setIsFetchingCategories(false);
      }, 800);
    }
  };

  const applyFilters = () => {
    setSelectedCategories(tempCategories);
    setPriceRange(tempPriceRange);
    setSortOrder(tempSortOrder);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setTempCategories([]);
    setTempPriceRange({ min: '', max: '' });
    setTempSortOrder('none');
  };

  const toggleTempCategory = (cat: string) => {
    setTempCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const processedProducts = useMemo(() => {
    let result = [...products];

    if (searchQuery) {
      const lowerQ = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(lowerQ) || 
        p.category.toLowerCase().includes(lowerQ)
      );
    }

    if (selectedCategories.length > 0) {
      result = result.filter(p => selectedCategories.includes(p.category));
    }

    const min = parseFloat(priceRange.min);
    const max = parseFloat(priceRange.max);
    if (!isNaN(min)) result = result.filter(p => p.currentPrice >= min);
    if (!isNaN(max)) result = result.filter(p => p.currentPrice <= max);

    if (sortOrder === 'asc') {
      result.sort((a, b) => a.currentPrice - b.currentPrice);
    } else if (sortOrder === 'desc') {
      result.sort((a, b) => b.currentPrice - a.currentPrice);
    }

    return result;
  }, [products, searchQuery, selectedCategories, priceRange, sortOrder]);

  return (
    <>
      <Hero />

      <div className="container mx-auto px-2 md:px-4 mt-8">
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-stone-50 border border-stone-200 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('store')}
              className={`px-8 py-2.5 rounded-lg text-sm font-semibold tracking-wide transition-all ${
                activeTab === 'store'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Store
            </button>
            <button
              onClick={() => setActiveTab('consult')}
              className={`px-8 py-2.5 rounded-lg text-sm font-semibold tracking-wide transition-all ${
                activeTab === 'consult'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Consult
            </button>
          </div>
        </div>

        {activeTab === 'store' && (
          <>
            <div className="bg-white/90 backdrop-blur p-3 md:p-4 border-b border-stone-200 sticky top-20 z-40 transition-all duration-500">
              <div className="container mx-auto max-w-5xl flex gap-3">
                <div className="flex flex-1 shadow-sm rounded-xl overflow-hidden border border-stone-200 bg-white">
                  <div className="bg-stone-50 text-stone-600 px-4 flex items-center justify-center border-r border-stone-200">
                    <Search size={18} />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search premium stones..."
                    className="flex-1 px-4 py-3 text-sm text-stone-900 focus:outline-none font-medium"
                  />
                </div>
                
                <button 
                  onClick={openFilterModal}
                  className="px-5 bg-stone-900 text-white rounded-xl shadow-sm hover:bg-stone-800 transition-colors flex items-center gap-2"
                >
                  <SlidersHorizontal size={18} />
                  <span className="hidden sm:block text-sm font-semibold tracking-wide">Filter</span>
                  {(selectedCategories.length > 0 || sortOrder !== 'none' || priceRange.min || priceRange.max) && (
                    <span className="w-2 h-2 rounded-full bg-orange-500 absolute top-3 right-3 sm:relative sm:top-auto sm:right-auto ml-1"></span>
                  )}
                </button>
              </div>
            </div>

            <div className="container mx-auto px-2 md:px-4 pb-20 mt-6">
              {isLoadingProducts && (
                <div className="py-16 text-center text-stone-500 text-sm font-medium tracking-widest uppercase">Loading collection…</div>
              )}

              {!isLoadingProducts && productsError && (
                <div className="py-16 text-center text-red-600 text-sm font-medium tracking-widest uppercase">{productsError}</div>
              )}

              {!isLoadingProducts && !productsError && processedProducts.length === 0 && (
                <div className="py-20 text-center flex flex-col items-center">
                  <Search size={40} className="text-stone-300 mb-4" />
                  <p className="text-stone-500 text-sm font-medium tracking-widest uppercase">No stones match your criteria.</p>
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                {processedProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'consult' && (
          <div className="container mx-auto px-2 md:px-4 pb-20">
            <Consult />
          </div>
        )}
      </div>

      {isFilterOpen && (
        <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-stone-900/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
          <div className="bg-white w-full sm:w-[450px] sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col shadow-2xl animate-[slideUp_0.3s_ease-out]">
            
            <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between shrink-0">
              <h2 className="text-xl font-serif text-stone-900 font-semibold">Filter & Sort</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-2 text-stone-400 hover:text-stone-900 transition-colors bg-stone-50 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-8">
              
              <section>
                <h3 className="text-sm font-semibold text-stone-900 mb-4 uppercase tracking-wider">Category</h3>
                {isFetchingCategories ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-orange-500" size={32} />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setTempCategories([])}
                      className={`px-4 py-2 text-sm rounded-xl border transition-all ${
                        tempCategories.length === 0 
                          ? 'bg-stone-900 text-white border-stone-900' 
                          : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                      }`}
                    >
                      All
                    </button>
                    {availableCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => toggleTempCategory(cat)}
                        className={`px-4 py-2 text-sm rounded-xl border transition-all ${
                          tempCategories.includes(cat)
                            ? 'bg-orange-50 text-orange-700 border-orange-200 font-medium'
                            : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-stone-900 mb-4 uppercase tracking-wider">Sort By Price</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setTempSortOrder(tempSortOrder === 'asc' ? 'none' : 'asc')}
                    className={`px-4 py-3 text-sm rounded-xl border transition-all ${
                      tempSortOrder === 'asc' ? 'bg-orange-50 border-orange-200 text-orange-700 font-medium' : 'bg-white border-stone-200 text-stone-600'
                    }`}
                  >
                    Low to High
                  </button>
                  <button
                    onClick={() => setTempSortOrder(tempSortOrder === 'desc' ? 'none' : 'desc')}
                    className={`px-4 py-3 text-sm rounded-xl border transition-all ${
                      tempSortOrder === 'desc' ? 'bg-orange-50 border-orange-200 text-orange-700 font-medium' : 'bg-white border-stone-200 text-stone-600'
                    }`}
                  >
                    High to Low
                  </button>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-stone-900 mb-4 uppercase tracking-wider">Price Range</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">₹</span>
                    <input
                      type="number"
                      placeholder="Min"
                      value={tempPriceRange.min}
                      onChange={(e) => setTempPriceRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-full pl-8 pr-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 font-medium"
                    />
                  </div>
                  <span className="text-stone-400 font-medium">to</span>
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">₹</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={tempPriceRange.max}
                      onChange={(e) => setTempPriceRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-full pl-8 pr-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 font-medium"
                    />
                  </div>
                </div>
              </section>

            </div>

            <div className="p-5 border-t border-stone-100 bg-stone-50 shrink-0 flex gap-3 sm:rounded-b-3xl">
              <button 
                onClick={clearFilters}
                className="flex-1 py-3.5 text-sm font-semibold text-stone-600 border border-stone-300 rounded-xl hover:bg-stone-100 transition-colors"
              >
                Clear All
              </button>
              <button 
                onClick={applyFilters}
                className="flex-[2] py-3.5 text-sm font-semibold text-white bg-orange-600 rounded-xl shadow-lg hover:bg-orange-700 transition-colors"
              >
                Show Results
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

/* ---------------- ADMIN GATEKEEPER ---------------- */

const AdminGatekeeper = ({ children }: { children: JSX.Element }) => {
  const { user } = useAuth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsChecking(false), 1000);
    return () => clearTimeout(timer);
  }, [user]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-orange-600 mb-4" size={32} />
        <p className="text-[10px] font-black uppercase tracking-widest text-stone-500">Verifying Admin Clearance...</p>
      </div>
    );
  }

  const isUserAdmin = user?.isConsultant === true || (user as any)?.isAdmin === true;

  if (!user || !isUserAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/* ---------------- APP CONTENT ---------------- */

const AppContent: React.FC = () => {
  const location = useLocation();
  const isDirectingToAdmin = location.pathname.toLowerCase().startsWith('/admin');
  
  const [isLoading, setIsLoading] = useState(!isDirectingToAdmin);
  const { isBanned, isLoading: banLoading, banReason } = useBanCheck();

  if (banLoading || (isLoading && !isBanned)) {
    return <AppLoadingScreen onComplete={() => setIsLoading(false)} />;
  }

  return (
    <>
      {isBanned && <BanModal banReason={banReason} />}
      
      {!isBanned && (
        <>
          <Header />
          <main className="flex-grow bg-[#FF9933]/5 min-h-screen transition-all duration-500">
            <Routes>
              <Route path="/" element={<HomeView />} />
              <Route path="/consult" element={<Consult />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/orders" element={<OrdersPage />} />
              
              <Route 
                path="/admin" 
                element={
                  <AdminGatekeeper>
                    <AdminPanel />
                  </AdminGatekeeper>
                } 
              />
              
              <Route path="/profile" element={<Profile />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/loading" element={<div className="h-screen bg-white" />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </>
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <NotificationProvider>
      <NavigationProvider>
        <AuthProvider>
          <SecurityProvider>
            <CartProvider>
              <div className="min-h-screen flex flex-col font-sans">
                <AppContent />
                <CartSidebar />
                <LoginModal />
              </div>
            </CartProvider>
          </SecurityProvider>
        </AuthProvider>
      </NavigationProvider>
    </NotificationProvider>
  );
};

export default App;
